import { type CSSProperties, type ReactNode, useMemo } from "react";
import { type SubstepType } from "~/features/configurator/Linear/dtos";
import type { CsticId, LinearCsticValue } from "~/features/configurator/Linear/models";
import { useCsticSelectProps } from "~/features/configurator/Shaft/components/useCsticSelectProps";

import classes from "~/features/configurator/Shaft/components/SimpleCsticTileField.module.less";
import classNames from "classnames";

interface Props {
  className?: string;
  csticId: CsticId;
  substepType: SubstepType;
  csticsToNull?: string[];
  csticsToSet?: Array<CsticToSet | Resolver>;
  allowedValues?: string[];
  renderTile: (props: RenderTileProps) => ReactNode | { component: ReactNode; span: number };
  tilesPerRow?: number;
  required?: boolean;
  onClick?: (value: LinearCsticValue) => void;
}

interface RenderTileProps {
  index: number;
  value: LinearCsticValue;
  onClick: () => void;
  selected?: boolean;
  disabled?: boolean;
  error?: boolean;
}

type CsticToSet =
  | {
      key: string;
      value: string | null;
    }
  | undefined;

type Resolver = (value: string | null) => CsticToSet;

export const SimpleCsticTileField = ({
  className,
  csticId,
  allowedValues,
  substepType,
  csticsToNull,
  csticsToSet,
  renderTile,
  tilesPerRow = 1,
  required,
  onClick: externalOnClick,
}: Props) => {
  const {
    selectProps: { onChange, disabled, value, error },
    csticValues,
  } = useCsticSelectProps({
    csticId,
    substep: substepType,
    allowedValues,
    csticsToNull,
    csticsToSet,
    required,
  });

  const tiles: { component: ReactNode; span: number; csticValue: LinearCsticValue }[] = useMemo(() => {
    return csticValues.map((csticValue, index) => {
      const result = renderTile({
        index,
        value: csticValue,
        onClick: () => {
          onChange(csticValue.value);
          externalOnClick?.(csticValue);
        },
        selected: csticValue.value === value,
        disabled,
        error,
      });
      if (!result) {
        return { component: null, span: 1, csticValue };
      }
      if (typeof result !== "object") {
        return { component: result, span: 1, csticValue };
      }
      if ("component" in result) {
        return { ...result, csticValue } as { component: ReactNode; span: number; csticValue: LinearCsticValue };
      }
      return { component: result, span: 1, csticValue };
    });
  }, [csticValues, renderTile]);

  return (
    <ul className={classNames(classes.root, className)} style={{ "--column-amount": tilesPerRow } as CSSProperties}>
      {tiles.map(({ component, span, csticValue }) => (
        <li key={csticValue.value} className={classes.tileContainer} style={{ "--cell-span": span } as CSSProperties}>
          {component}
        </li>
      ))}
    </ul>
  );
};
